import React from "react";

/**
 * Props interface for the Avatar component
 */
interface AvatarProps {
  /** URL of the image to display */
  imageUrl?: string;
  /** Alt text for the image */
  altText?: string;
  /** Size of the avatar (default or custom size) */
  size?: "sm" | "md" | "lg" | number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Avatar component to display a user or organization image
 *
 * @param {AvatarProps} props - Component props
 * @returns {JSX.Element} Avatar component
 */
const Avatar = ({
  imageUrl = "/images/organization-avatar.png",
  altText = "Avatar",
  size = "md",
  className = "",
}: AvatarProps): JSX.Element => {
  // Determine size classes based on size prop
  let sizeClasses = "";

  if (typeof size === "string") {
    switch (size) {
      case "sm":
        sizeClasses = "w-[40px] h-[40px] sm:w-[50px] sm:h-[50px]";
        break;
      case "lg":
        sizeClasses = "w-[80px] h-[80px] sm:w-[100px] sm:h-[100px]";
        break;
      case "md":
      default:
        sizeClasses = "w-[60px] h-[60px] sm:w-[80px] sm:h-[80px]";
        break;
    }
  } else {
    // Custom size
    sizeClasses = `w-[${size}px] h-[${size}px]`;
  }

  return (
    <div
      className={`${sizeClasses} rounded-[50%] overflow-hidden border-2 border-solid border-[#E0E0E0] flex-shrink-0 ${className}`}
      role="img"
      aria-label={altText}
    >
      <img
        src={imageUrl}
        alt={altText}
        className="w-full h-full object-cover"
      />
    </div>
  );
};

export default Avatar;
